import { useEffect, useState } from 'react';
import type { StickerPack, StickerRef } from '@kinop/shared';
import { listStickerPacks } from '../lib/api.js';
import { StickerImage } from './StickerPicker.js';
import { Button } from '@/components/ui/button';

interface BadgePickerProps {
  badge: StickerRef | null | undefined;
  onChange: (badge: StickerRef | null) => void;
}

/**
 * Значок рядом с именем.
 *
 * Берётся из наборов премиум-эмодзи — тех, что добавляются ссылкой
 * `t.me/addemoji/…`. Обычные стикеры сюда не годятся: рядом с именем они
 * размером с букву, и от картинки остаётся пятно.
 */
export function BadgePicker({ badge, onChange }: BadgePickerProps): JSX.Element {
  const [packs, setPacks] = useState<StickerPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void listStickerPacks()
      .then((result) => {
        if (cancelled) return;
        setPacks(result.packs.filter((pack) => pack.kind === 'custom_emoji'));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) return <p className="stickers__empty">Загружаем…</p>;

  if (packs.length === 0) {
    return (
      <p className="stickers__hint">
        Наборов премиум-эмодзи пока нет. Добавьте любой ссылкой вида
        <code> t.me/addemoji/…</code> — на вкладке «+» в панели стикеров, — и он появится
        здесь.
      </p>
    );
  }

  return (
    <div className="badge-picker">
      {packs.map((pack) => (
        <div key={pack.name} className="badge-picker__pack">
          <span className="mono-label">{pack.title}</span>
          <div className="stickers__grid stickers__grid--emoji">
            {pack.stickers.map((sticker) => (
              <button
                key={sticker.id}
                type="button"
                className="stickers__item"
                data-on={badge?.id === sticker.id ? '' : undefined}
                aria-pressed={badge?.id === sticker.id}
                aria-label={`Значок ${sticker.emoji ?? ''}`.trim()}
                onClick={() => onChange(badge?.id === sticker.id ? null : sticker)}
              >
                <StickerImage sticker={sticker} />
              </button>
            ))}
          </div>
        </div>
      ))}

      {badge && (
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          Убрать значок
        </Button>
      )}
    </div>
  );
}
